import React, { useEffect, useState, useCallback } from 'react';
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridColDef
} from '@mui/x-data-grid';
import { TextField, Button, Select, MenuItem, Box, CircularProgress, Snackbar, Alert, Paper, Card, CardContent, Typography, Chip, AlertColor, Tooltip, Stack, Badge, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText } from '@mui/material';
import { apiUrl } from '../utils/api';
import { dataGridSx } from '../utils/dataGridStyles';
import { useBeforeUnload } from 'react-router-dom';
import usePrompt from '../hooks/usePrompt';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import WarningIcon from '@mui/icons-material/Warning';
import PeopleIcon from '@mui/icons-material/People';

const PAGE_GROUPS = [
  {
    label: 'Design Team',
    pages: [
      'Dashboard - Design',
      'Design Changes',
      'Delete All Design Changes',
      'Documents',
      'Document Settings',
      'Delete All Documents',
      'Delete All Document Locations',
      'Delete All Document Categories',
      'Delete All Document Statuses',
      'Assignments',
      'Delete All Assignments'
    ]
  },
  {
    label: 'Project Settings',
    pages: [
      'Units',
      'BOQ Line Items',
      'Delete All BOQ Line Items',
      'Unit Rate',
      'BOQ Categories',
      'Delete All BOQ Categories',
      'Delete All Units',
      'Delete All Rate Sheets',
      'Delete All Rate Sheets items'
      
      
    ]
  },
  {
    label: 'Site Team',
    pages: ['Dashboard - Site', 'Site Tracker', 'Material Requisitions', 'Delete All Material Requisitions']
  },
  {
    label: 'QS Team',
    pages: ['Dashboard - QS', 'QS Design Change', 'QS Purchase Orders', 'QS Contract or VO', 'Delete All QS Contract or VO']
  },
  { label: 'Project Management', pages: [/* 'Contracts', 'Contract Design Changes', 'Delete All Contracts', "Delete All Contract Design Changes" */ 'MR/PO Approval', 'Contract/VO Approval'] },
  { label: 'Settings', pages: ['Projects','Groups', 'Users'] },
  {
    label: 'AI Assistant',
    pages: [
      'AI Assistant - Chat',
      'AI Assistant - Settings'
    ]
  }
];

const ALL_PAGES = PAGE_GROUPS.flatMap(g => g.pages);

const Groups = ({ access = 'none', refreshAccess, companyId = null }) => {
  const readOnly = access !== 'write';
  const [groups, setGroups] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [newGroup, setNewGroup] = useState({ group_description: '', project_ids: [], company_id: null });
  const [selectedId, setSelectedId] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    groupId: number | null;
    groupName: string;
    hasWarning: boolean;
    userCount: number;
    userEmails: string[];
  }>({
    open: false,
    groupId: null,
    groupName: '',
    hasWarning: false,
    userCount: 0,
    userEmails: []
  });

  const [manageUsersDialog, setManageUsersDialog] = useState<{
    open: boolean;
    groupId: number | null;
    groupName: string;
    users: any[];
    loading: boolean;
    saving: boolean;
    searchTerm: string;
  }>({
    open: false,
    groupId: null,
    groupName: '',
    users: [],
    loading: false,
    saving: false,
    searchTerm: ''
  });

  usePrompt('You have unsaved permission changes. Are you sure you want to leave?', dirty);
  useBeforeUnload(
    useCallback(
      e => {
        if (dirty) {
          e.preventDefault();
          e.returnValue = '';
        }
      },
      [dirty]
    )
  );

  const fetchGroups = () => {
    let url = apiUrl('/api/groups');
    const params = new URLSearchParams();

    // Filter by selected company if companyId is provided
    if (companyId) {
      params.append('company_id', companyId.toString());
    }

    if (params.toString()) {
      url += '?' + params.toString();
    }

    return fetch(url, {
      credentials: 'include'  // Include cookies for session auth
    })
      .then(res => res.json())
      .then(data => {
        console.log('[Groups] Fetched groups:', data);
        // Handle both array response and object with groups property
        const groupsData = Array.isArray(data) ? data : (data.groups || []);
        setGroups(groupsData);
      })
      .catch(err => {
        console.error('[Groups] Error fetching groups:', err);
        setGroups([]);
      });
  };

  const fetchProjects = () => {
    let url = apiUrl('/api/projects');
    const params = new URLSearchParams();

    // Filter by selected company if companyId is provided
    if (companyId) {
      params.append('company_id', companyId.toString());
    }

    if (params.toString()) {
      url += '?' + params.toString();
    }

    return fetch(url, {
      credentials: 'include'  // Include cookies for session auth
    })
      .then(res => res.json())
      .then(data => setProjects(data.projects || []))
      .catch(() => setProjects([]));
  };

  const fetchCompanies = () => {
    return fetch(apiUrl('/api/companies'), {
      credentials: 'include'  // Include cookies for session auth
    })
      .then(res => res.json())
      .then(data => setCompanies(data.companies || []))
      .catch(() => setCompanies([]));
  };

  const fetchPerms = id => {
    fetch(apiUrl(`/api/groups/${id}/permissions`), {
      credentials: 'include'  // Include cookies for session auth
    })
      .then(res => res.json())
      .then(list => {
        const obj = {};
        list.forEach(p => { obj[p.page_name] = p.access_type; });
        setPermissions(obj);
        setDirty(false);
      })
      .catch(() => setPermissions({}));
  };

  useEffect(() => {
    console.log('[Groups] Groups state updated, count:', groups.length, groups);
  }, [groups]);

  useEffect(() => {
    if (access === 'none') {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        console.log('[Groups] Starting data fetch...');
        await Promise.all([
          fetchGroups(),
          fetchProjects(),
          fetchCompanies()
        ]);
        console.log('[Groups] Data fetch completed');
      } catch (error) {
        console.error('[Groups] Error loading data:', error);
      } finally {
        console.log('[Groups] Setting loading to false');
        setLoading(false);
      }
    };

    loadData();
  }, [access, companyId]);

  const savePerms = useCallback(() => {
    if (readOnly) return Promise.resolve();
    const perms = ALL_PAGES.map(p => ({
      page_name: p,
      access_type: permissions[p] || 'none'
    }));
    return fetch(apiUrl(`/api/groups/${selectedId}/permissions`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',  // Include cookies for session auth
      body: JSON.stringify({ permissions: perms })
    }).then(() => {
      fetchPerms(selectedId);
      if (refreshAccess) refreshAccess();
      setDirty(false);
    });
  }, [permissions, readOnly, selectedId, refreshAccess]);

  if (access === 'none') {
    return <div>Access denied</div>;
  }

  const showToast = (message: string, severity: AlertColor = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleAdd = async () => {
    if (readOnly) return;

    // Validation
    if (!newGroup.group_description.trim()) {
      showToast('Group description is required', 'error');
      return;
    }

    if (!newGroup.company_id) {
      showToast('Company selection is required', 'error');
      return;
    }

    // NOTE: Project selection validation is done on backend
    // Only Global Admin and Company Admin can create groups without projects (Company Admin groups)
    // Project-level users must select at least one project

    try {
      setAdding(true);
      console.log('[Groups] Creating group:', newGroup);
      console.log('[Groups] project_ids ARRAY:', newGroup.project_ids);
      console.log('[Groups] project_ids LENGTH:', newGroup.project_ids.length);
      console.log('[Groups] project_ids TYPE:', typeof newGroup.project_ids, Array.isArray(newGroup.project_ids));

      const response = await fetch(apiUrl('/api/groups'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newGroup)
      });

      const data = await response.json();
      console.log('[Groups] Create response:', { status: response.status, data });

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add group');
      }

      // Clear form
      setNewGroup({ group_description: '', project_ids: [], company_id: null });

      // Add small delay for database commit
      await new Promise(resolve => setTimeout(resolve, 100));

      // Refresh groups list
      console.log('[Groups] Refreshing groups list...');
      await fetchGroups();
      console.log('[Groups] Groups list refreshed');

      showToast('Group added successfully!', 'success');
    } catch (error) {
      console.error('[Groups] Error adding group:', error);
      showToast(error.message || 'Failed to add group', 'error');
    } finally {
      setAdding(false);
    }
  };

  const handleRowClick = params => {
    if (params.id === selectedId) return;
    const load = () => {
      setSelectedId(params.id);
      fetchPerms(params.id);
    };
    if (dirty) {
      if (window.confirm('You have unsaved permission changes. Save them?')) {
        savePerms().then(load);
      } else {
        setDirty(false);
        load();
      }
    } else {
      load();
    }
  };

  const handleEditClick = id => () => {
    if (readOnly) return;
    const group = groups.find(g => g.group_id === id);
    if (group?.is_system_default) {
      showToast('The Global Admin group cannot be edited', 'error');
      return;
    }

    // Enter edit mode with field values initialized
    // This ensures company_id and project_ids are properly set in edit state
    setRowModesModel({
      ...rowModesModel,
      [id]: {
        mode: GridRowModes.Edit,
        fieldToFocus: 'description' // Focus on description field
      }
    });
  };

  const handleSaveClick = id => () => {
    if (readOnly) return;
    setSaving(id);
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = id => () => {
    if (readOnly) return;
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true }
    });
  };

  const handleDeleteClick = (id) => () => {
    if (readOnly) return;
    const group = groups.find(g => g.group_id === id);

    if (group?.is_system_default) {
      showToast('The Global Admin group cannot be deleted', 'error');
      return;
    }

    setDeleteDialog({
      open: true,
      groupId: id,
      groupName: group?.group_name || 'Unknown Group',
      hasWarning: false,
      userCount: 0,
      userEmails: []
    });
  };

  const handleDeleteConfirm = async () => {
    const { groupId, hasWarning } = deleteDialog;
    if (!groupId) return;

    try {
      setDeleting(groupId);
      const url = hasWarning
        ? apiUrl(`/api/groups/${groupId}?force=true`)
        : apiUrl(`/api/groups/${groupId}`);

      const response = await fetch(url, {
        method: 'DELETE',
        credentials: 'include'
      });

      // Handle 409 - group has users assigned
      if (response.status === 409) {
        const data = await response.json();
        // Update dialog to show warning
        setDeleteDialog(prev => ({
          ...prev,
          hasWarning: true,
          userCount: data.user_count || 0,
          userEmails: data.user_emails || []
        }));
        setDeleting(null);
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Delete failed');
      }

      // Close dialog
      setDeleteDialog({
        open: false,
        groupId: null,
        groupName: '',
        hasWarning: false,
        userCount: 0,
        userEmails: []
      });

      await fetchGroups();

      // Clear selection if deleted group was selected
      if (selectedId === groupId) {
        setSelectedId(null);
        setPermissions({});
      }

      showToast('Group deleted successfully!', 'success');
    } catch (error) {
      console.error('[Groups] Error deleting group:', error);
      showToast(error.message || 'Failed to delete group', 'error');
      setDeleteDialog(prev => ({ ...prev, open: false }));
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      groupId: null,
      groupName: '',
      hasWarning: false,
      userCount: 0,
      userEmails: []
    });
    setDeleting(null);
  };

  const handleManageUsersClick = (groupId: number, groupName: string) => async () => {
    // Open dialog and fetch users
    setManageUsersDialog({
      open: true,
      groupId,
      groupName,
      users: [],
      loading: true,
      saving: false,
      searchTerm: ''
    });

    try {
      const response = await fetch(apiUrl(`/api/groups/${groupId}/users`), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Add was_in_group to track original state
        const usersWithOriginalState = (data.users || []).map(u => ({
          ...u,
          was_in_group: u.in_group
        }));
        setManageUsersDialog(prev => ({
          ...prev,
          users: usersWithOriginalState,
          loading: false
        }));
      } else {
        throw new Error('Failed to load users');
      }
    } catch (error) {
      console.error('[Groups] Error loading users:', error);
      showToast('Failed to load users', 'error');
      setManageUsersDialog(prev => ({
        ...prev,
        loading: false
      }));
    }
  };

  const handleManageUsersClose = () => {
    setManageUsersDialog({
      open: false,
      groupId: null,
      groupName: '',
      users: [],
      loading: false,
      saving: false,
      searchTerm: ''
    });
  };

  const handleUserToggle = (userId: number) => {
    setManageUsersDialog(prev => ({
      ...prev,
      users: prev.users.map(user =>
        user.user_id === userId
          ? { ...user, in_group: !user.in_group }
          : user
      )
    }));
  };

  const handleManageUsersSave = async () => {
    if (!manageUsersDialog.groupId) return;

    setManageUsersDialog(prev => ({ ...prev, saving: true }));

    try {
      const usersToAdd = manageUsersDialog.users
        .filter(u => u.in_group && !u.was_in_group)
        .map(u => u.user_id);

      const usersToRemove = manageUsersDialog.users
        .filter(u => !u.in_group && u.was_in_group)
        .map(u => u.user_id);

      // Add users
      if (usersToAdd.length > 0) {
        const addResponse = await fetch(apiUrl(`/api/groups/${manageUsersDialog.groupId}/users`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ user_ids: usersToAdd })
        });

        if (!addResponse.ok) {
          throw new Error('Failed to add users');
        }
      }

      // Remove users
      for (const userId of usersToRemove) {
        const removeResponse = await fetch(
          apiUrl(`/api/groups/${manageUsersDialog.groupId}/users/${userId}`),
          {
            method: 'DELETE',
            credentials: 'include'
          }
        );

        if (!removeResponse.ok) {
          throw new Error('Failed to remove user');
        }
      }

      showToast('Users updated successfully', 'success');
      handleManageUsersClose();

      // Refresh groups list to update user counts
      await fetchGroups();
    } catch (error) {
      console.error('[Groups] Error saving users:', error);
      showToast('Failed to update users', 'error');
    } finally {
      setManageUsersDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const processRowUpdate = async (updatedRow) => {
    const { group_id, group_name, description, company_id, project_ids } = updatedRow;
    if (groups.find(group => group.group_id === group_id)?.is_system_default) {
      throw new Error('The Global Admin group cannot be edited');
    }

    // Validation
    if (!group_name || !group_name.trim()) {
      showToast('Group name cannot be empty', 'error');
      throw new Error('Group name cannot be empty');
    }

    // Validate that all selected projects belong to the group's company
    if (company_id && project_ids && project_ids.length > 0) {
      const invalidProjects = project_ids.filter(projectId => {
        const project = projects.find(p => p.project_id === projectId);
        return project && project.company_id !== company_id;
      });

      if (invalidProjects.length > 0) {
        const company = companies.find(c => c.company_id === company_id);
        showToast(`Selected projects must belong to ${company?.company_name || 'the group\'s company'}`, 'error');
        throw new Error('Invalid projects selected');
      }
    }

    try {
      setSaving(group_id);

      const response = await fetch(apiUrl(`/api/groups/${group_id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          group_name,
          description,
          company_id,
          project_ids
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Update failed');

      await fetchGroups();
      showToast('Group updated successfully!', 'success');
      return updatedRow;
    } catch (err) {
      showToast(err.message || 'Failed to update group', 'error');
      throw err;
    } finally {
      setSaving(null);
    }
  };

  const handlePermChange = (page, value) => {
    if (readOnly) return;
    setPermissions({ ...permissions, [page]: value });
    setDirty(true);
  };

  const handleBulkPermChange = (pages, value) => {
    if (readOnly) return;
    const updatedPerms = { ...permissions };
    pages.forEach(page => {
      updatedPerms[page] = value;
    });
    setPermissions(updatedPerms);
    setDirty(true);
  };



  const columns: GridColDef[] = [
    {
      field: 'group_id',
      headerName: 'ID',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: params => (
        <Chip
          label={`#${params.value}`}
          size="medium"
          color="primary"
          variant="outlined"
          onClick={(e) => e.stopPropagation()}
          sx={{
            fontWeight: 600,
            fontSize: '0.85rem',
            height: 28
          }}
        />
      )
    },
    {
      field: 'group_name',
      headerName: 'Group Name',
      flex: 1.5,
      minWidth: 200,
      editable: !readOnly,
      renderCell: params => {
        const isEditing = rowModesModel[params.row.group_id]?.mode === GridRowModes.Edit;
        if (isEditing) return null; // Let DataGrid handle edit mode

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.95rem',
                color: 'text.primary'
              }}
            >
              {params.value}
            </Typography>
            {/* DISABLED temporarily - System badge
            {params.row.is_system_default && (
              <Chip
                label="System"
                size="small"
                color="error"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
              />
            )}
            */}
          </Box>
        );
      }
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2.5,
      minWidth: 250,
      editable: !readOnly,
      renderCell: params => {
        const isEditing = rowModesModel[params.row.group_id]?.mode === GridRowModes.Edit;
        if (isEditing) return null;

        return (
          <Tooltip title={params.value || 'No description'} placement="top" arrow>
            <Typography
              variant="body2"
              sx={{
                color: params.value ? 'text.secondary' : 'text.disabled',
                fontStyle: params.value ? 'normal' : 'italic',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}
            >
              {params.value || 'No description'}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'company_id',
      headerName: 'Company',
      flex: 1.3,
      minWidth: 180,
      editable: !readOnly,
      valueGetter: (value, row) => {
        // Return company name for filtering to work
        const company = companies.find(c => c.company_id === row.company_id);
        return company ? `${company.company_code} - ${company.company_name}` : 'Unassigned';
      },
      valueSetter: (value, row) => {
        // Store the raw company_id, not the display string
        return { ...row, company_id: value };
      },
      preProcessEditCellProps: (params) => {
        // Initialize edit cell with current row's company_id
        console.log('[Groups] Company preProcessEditCellProps:', {
          rowId: params.id,
          rowCompanyId: params.row.company_id,
          propsValue: params.props.value
        });
        return { ...params.props, value: params.row.company_id };
      },
      renderCell: params => {
        const companyId = params.row.company_id;
        const company = companies.find(c => c.company_id === companyId);

        if (!company) {
          return (
            <Chip
              icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
              label="Unassigned"
              size="medium"
              variant="outlined"
              color="default"
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontWeight: 500,
                fontStyle: 'italic',
                color: 'text.secondary',
                cursor: 'default',
                height: 28,
                fontSize: '0.85rem'
              }}
            />
          );
        }

        return (
          <Tooltip title={company.company_name} placement="top" arrow>
            <span>
              <Chip
                icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
                label={`${company.company_code}`}
                size="medium"
                color="primary"
                variant="filled"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  fontWeight: 500,
                  maxWidth: '100%',
                  cursor: 'default',
                  height: 28,
                  fontSize: '0.85rem',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    paddingLeft: '8px',
                    paddingRight: '12px'
                  }
                }}
              />
            </span>
          </Tooltip>
        );
      },
      renderEditCell: params => {
        // Use params.value which is set by preProcessEditCellProps
        const currentValue = params.value;

        console.log('[Groups Edit] Company renderEditCell:', {
          rowId: params.id,
          'params.value': params.value,
          'params.row.company_id': params.row.company_id,
          currentValue
        });

        return (
          <Select
            value={currentValue ?? ''}
            onChange={e => {
              const newCompanyId = e.target.value === '' ? null : Number(e.target.value);

              console.log('[Groups Edit] Company onChange:', {
                from: currentValue,
                to: newCompanyId
              });

              // Set the new company value
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newCompanyId
              });

              // Clear projects if company changed
              if (currentValue !== newCompanyId) {
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'project_ids',
                  value: []
                });
              }
            }}
            size="small"
            displayEmpty
            sx={{ width: '100%' }}
            renderValue={(selected) => {
              if (!selected) {
                return <em style={{ color: '#666' }}>Select Company</em>;
              }
              const company = companies.find(c => c.company_id === selected);
              return company ? `${company.company_code} - ${company.company_name}` : 'Select Company';
            }}
          >
            <MenuItem value="">
              <em>No Company</em>
            </MenuItem>
            {companies.map(c => (
              <MenuItem key={c.company_id} value={c.company_id}>
                {c.company_code} - {c.company_name}
              </MenuItem>
            ))}
          </Select>
        );
      }
    },
    {
      field: 'project_ids',
      headerName: 'Projects',
      flex: 2.5,
      minWidth: 280,
      editable: !readOnly,
      valueSetter: (value, row) => {
        // Store the raw project_ids array, not the display string
        return { ...row, project_ids: value };
      },
      preProcessEditCellProps: (params) => {
        // Initialize edit cell with current row's project_ids
        console.log('[Groups] Projects preProcessEditCellProps:', {
          rowId: params.id,
          rowProjectIds: params.row.project_ids,
          propsValue: params.props.value
        });
        return { ...params.props, value: params.row.project_ids || [] };
      },
      valueGetter: (value, row) => {
        // Return project names for filtering to work
        const ids = row.project_ids || [];
        if (ids.length === 0) {
          const hasCompany = row.company_id !== null && row.company_id !== undefined;
          return hasCompany ? 'All projects' : 'No projects';
        }
        return ids
          .map(pid => {
            const proj = projects.find(p => p.project_id === pid);
            return proj ? `${proj.project_number} - ${proj.project_name}` : '';
          })
          .filter(Boolean)
          .join(', ');
      },
      renderCell: params => {
        const ids = params.row.project_ids || [];
        const hasCompany = params.row.company_id !== null && params.row.company_id !== undefined;

        if (ids.length === 0) {
          return (
            <Chip
              label={hasCompany ? "All projects" : "No projects"}
              size="medium"
              variant="outlined"
              onClick={(e) => e.stopPropagation()}
              sx={{
                fontStyle: hasCompany ? 'normal' : 'italic',
                color: hasCompany ? 'success.main' : 'text.secondary',
                fontWeight: hasCompany ? 600 : 400,
                cursor: 'default',
                height: 28,
                fontSize: '0.85rem'
              }}
            />
          );
        }

        const projectChips = ids.slice(0, 2).map(pid => {
          const proj = projects.find(p => p.project_id === pid);
          if (!proj) return null;
          return (
            <Tooltip key={pid} title={proj.project_name} placement="top" arrow>
              <span>
                <Chip
                  icon={<FolderIcon sx={{ fontSize: '1rem' }} />}
                  label={proj.project_number}
                  size="medium"
                  color="secondary"
                  variant="outlined"
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    cursor: 'default',
                    height: 28,
                    '& .MuiChip-label': {
                      paddingLeft: '8px',
                      paddingRight: '12px'
                    }
                  }}
                />
              </span>
            </Tooltip>
          );
        }).filter(Boolean);

        return (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
            {projectChips}
            {ids.length > 2 && (
              <Tooltip
                title={ids.slice(2).map(pid => {
                  const proj = projects.find(p => p.project_id === pid);
                  return proj ? proj.project_name : '';
                }).filter(Boolean).join(', ')}
                placement="top"
                arrow
              >
                <span>
                  <Chip
                    label={`+${ids.length - 2}`}
                    size="medium"
                    color="default"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 28,
                      minWidth: '32px',
                      cursor: 'default'
                    }}
                  />
                </span>
              </Tooltip>
            )}
          </Stack>
        );
      },
      renderEditCell: params => {
        // Get current company_id - check if it was just edited
        const editedRow = params.api.getRowWithUpdatedValues(params.id, 'company_id');
        const currentCompanyId = editedRow?.company_id ?? params.row.company_id;

        // Use params.value which is set by preProcessEditCellProps
        const selectedIds = Array.isArray(params.value)
          ? [...params.value]
          : [];

        // Filter projects by company
        const filteredProjects = currentCompanyId
          ? projects.filter(p => p.company_id === currentCompanyId)
          : projects;

        console.log('[Groups Edit] Projects renderEditCell:', {
          rowId: params.id,
          'params.value': params.value,
          'params.value isArray': Array.isArray(params.value),
          'params.row.project_ids': params.row.project_ids,
          currentCompanyId,
          selectedIds,
          allProjectsCount: projects.length,
          filteredProjectsCount: filteredProjects.length,
          filteredProjects: filteredProjects.slice(0, 3).map(p => ({ id: p.project_id, name: p.project_name, company_id: p.company_id }))
        });

        return (
          <Select
            multiple
            value={selectedIds}
            onChange={e => {
              const newValue = typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value;

              console.log('[Groups Edit] Projects onChange:', {
                from: selectedIds,
                to: newValue
              });

              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue
              });
            }}
            size="small"
            displayEmpty
            sx={{ width: '100%' }}
            renderValue={(selected) => {
              if (!Array.isArray(selected) || selected.length === 0) {
                return <em style={{ color: '#999' }}>Select projects</em>;
              }
              return selected
                .map(pid => {
                  const proj = projects.find(p => p.project_id === pid);
                  return proj ? `${proj.project_number}` : '';
                })
                .filter(Boolean)
                .join(', ');
            }}
          >
            {filteredProjects.length === 0 ? (
              <MenuItem disabled>
                <em>No projects available for this company</em>
              </MenuItem>
            ) : (
              filteredProjects.map(p => (
                <MenuItem key={p.project_id} value={p.project_id}>
                  <Checkbox checked={selectedIds.indexOf(p.project_id) > -1} />
                  {p.project_number} - {p.project_name}
                </MenuItem>
              ))
            )}
          </Select>
        );
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: ({ id, row }) => {
        if (readOnly) return [];
        const isEditing = rowModesModel[id]?.mode === GridRowModes.Edit;
        const isDeleting = deleting === id;
        const isSaving = saving === id;
        const isSystemDefault = Boolean(row.is_system_default);

        if (isEditing) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title={isSaving ? "Saving..." : "Save"}>{isSaving ? <CircularProgress size={20} /> : <SaveIcon />}</Tooltip>}
              label={isSaving ? "Saving..." : "Save"}
              onClick={handleSaveClick(id)}
              disabled={isSaving || isDeleting}
              showInMenu={false}
            />,
            <GridActionsCellItem
              icon={<Tooltip title="Cancel"><CancelIcon /></Tooltip>}
              label="Cancel"
              onClick={handleCancelClick(id)}
              disabled={isSaving || isDeleting}
              showInMenu={false}
            />
          ];
        }
        // Show saving state in view mode
        if (isSaving) {
          return [
            <GridActionsCellItem
              icon={<Tooltip title="Saving..."><CircularProgress size={20} /></Tooltip>}
              label="Saving..."
              onClick={() => {}}
              disabled={true}
              showInMenu={false}
            />
          ];
        }

        const actions = [
          <GridActionsCellItem
            icon={<Tooltip title="Manage Users"><PeopleIcon /></Tooltip>}
            label="Manage Users"
            onClick={handleManageUsersClick(id as number, row.group_name)}
            showInMenu={false}
            disabled={isDeleting || isSaving}
          />,
          <GridActionsCellItem
            icon={<Tooltip title={isSystemDefault ? "Cannot edit system group" : "Edit"}><EditIcon /></Tooltip>}
            label={isSystemDefault ? "Cannot edit system group" : "Edit"}
            onClick={handleEditClick(id)}
            showInMenu={false}
            disabled={isDeleting || isSaving || isSystemDefault}
          />,
          <GridActionsCellItem
            icon={<Tooltip title={isSystemDefault ? "Cannot delete system group" : (isDeleting ? "Deleting..." : "Delete")}>{isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}</Tooltip>}
            label={isSystemDefault ? "Cannot delete system group" : (isDeleting ? "Deleting..." : "Delete")}
            onClick={handleDeleteClick(id)}
            showInMenu={false}
            disabled={isDeleting || isSaving || isSystemDefault}
          />
        ];

        return actions;
      }
    }
  ];

  return (
    <div style={{ padding: "10px 24px 24px 24px", height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', overflow: 'hidden' }}>

      {!readOnly && (
        <Card sx={{ mb: 3, flexShrink: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Add New Group
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Group names are auto-generated: <strong>COMPANYCODE-Description</strong> (company admin group), <strong>COMPANYCODE-ProjectNumber-Description</strong> (single project group), or <strong>COMPANYCODE-ProjectCount-Description</strong> (multiple projects group)
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <Select
                value={newGroup.company_id || ''}
                onChange={e => setNewGroup({
                  ...newGroup,
                  company_id: e.target.value || null,
                  project_ids: []
                })}
                size="small"
                displayEmpty
                sx={{ minWidth: 200 }}
                disabled={adding}
                required
              >
                <MenuItem value="">
                  <em>Select Company (Required)</em>
                </MenuItem>
                {companies.map(c => (
                  <MenuItem key={c.company_id} value={c.company_id}>
                    {c.company_code} - {c.company_name}
                  </MenuItem>
                ))}
              </Select>
              <Select
                multiple
                value={newGroup.project_ids}
                onChange={e => setNewGroup({ ...newGroup, project_ids: Array.isArray(e.target.value) ? e.target.value : [] })}
                size="small"
                displayEmpty
                sx={{ minWidth: 200 }}
                disabled={adding || !newGroup.company_id}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>Select Projects (Optional)</em>;
                  }
                  return selected
                    .map(pid => {
                      const proj = projects.find(p => p.project_id === pid);
                      return proj ? `${proj.project_number}` : '';
                    })
                    .filter(Boolean)
                    .join(', ');
                }}
              >
                {projects
                  .filter(p => !newGroup.company_id || p.company_id === newGroup.company_id)
                  .map(p => (
                    <MenuItem key={p.project_id} value={p.project_id}>
                      {p.project_number} - {p.project_name}
                    </MenuItem>
                  ))}
              </Select>
              <TextField
                label="Group Description"
                required
                value={newGroup.group_description}
                onChange={e => setNewGroup({ ...newGroup, group_description: e.target.value })}
                sx={{ minWidth: 250, flex: 1 }}
                size="small"
                disabled={adding}
                placeholder="e.g., Engineers, Managers, QS Team"
                helperText="This will be appended to company/project codes"
              />
              <Button
                variant="contained"
                onClick={handleAdd}
                disabled={adding || !newGroup.group_description.trim() || !newGroup.company_id}
                startIcon={adding ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                sx={{ minWidth: 120 }}
              >
                {adding ? 'Adding...' : 'Add Group'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      <Paper elevation={2} sx={{
        flex: selectedId ? '1 1 50%' : '1 1 auto',
        width: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {loading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: 300
          }}>
            <CircularProgress size={50} />
          </Box>
        ) : (
          <DataGrid
            rows={groups}
            columns={columns}
            getRowId={r => r.group_id}
            onRowClick={handleRowClick}
            onRowDoubleClick={params => handleEditClick(params.id)()}
            isCellEditable={params => !readOnly && !params.row.is_system_default}
            editMode={readOnly ? undefined : 'row'}
            rowModesModel={rowModesModel}
            onRowModesModelChange={setRowModesModel}
            processRowUpdate={readOnly ? undefined : processRowUpdate}
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } }
            }}
            rowHeight={60}
            getRowClassName={(params) => (saving === params.row.group_id ? 'saving-row' : '')}
            rowSelectionModel={selectedId ? { type: 'include', ids: new Set([selectedId]) } : { type: 'include', ids: new Set() }}
            sx={{
              ...dataGridSx,
              height: '100%',
              '& .MuiDataGrid-row.saving-row': {
                opacity: 0.6,
                pointerEvents: 'none',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 1
                }
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                minHeight: '52px'
              }
            }}
          />
        )}
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      {selectedId && (
        <Paper elevation={2} sx={{
          marginTop: 3,
          flex: '1 1 auto',
          overflow: 'auto',
          minHeight: 0,
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Page Permissions
            </Typography>
            {dirty && (
              <Chip label="Unsaved Changes" color="warning" size="small" />
            )}
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 2
          }}>
            {PAGE_GROUPS.map(group => (
              <Card key={group.label} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                  {group.label}
                </Typography>
                {!readOnly && (
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => handleBulkPermChange(group.pages, 'write')}
                      sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Write All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => handleBulkPermChange(group.pages, 'read')}
                      sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      Read All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleBulkPermChange(group.pages, 'none')}
                      sx={{
                        fontSize: '0.75rem',
                        py: 0.5,
                        px: 1.5,
                        minWidth: 'auto',
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      None All
                    </Button>
                  </Stack>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {group.pages.map(page => (
                    <Box key={page} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem', flex: 1 }}>
                        {page}
                      </Typography>
                      <Select
                        value={permissions[page] || 'none'}
                        onChange={e => handlePermChange(page, e.target.value)}
                        size="small"
                        disabled={readOnly}
                        sx={{ minWidth: 90 }}
                      >
                        <MenuItem value="none">None</MenuItem>
                        <MenuItem value="read">Read</MenuItem>
                        <MenuItem value="write">Write</MenuItem>
                      </Select>
                    </Box>
                  ))}
                </Box>
              </Card>
            ))}
          </Box>
          {!readOnly && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={savePerms}
                disabled={!dirty}
                startIcon={<SaveIcon />}
              >
                Save Permissions
              </Button>
              {dirty && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (window.confirm('Discard unsaved changes?')) {
                      fetchPerms(selectedId);
                    }
                  }}
                  startIcon={<CancelIcon />}
                >
                  Discard Changes
                </Button>
              )}
            </Box>
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: deleteDialog.hasWarning ? 'error.lighter' : 'background.paper'
        }}>
          {deleteDialog.hasWarning && (
            <WarningIcon sx={{ color: 'error.main', fontSize: 28 }} />
          )}
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {deleteDialog.hasWarning ? 'Warning: Users Assigned' : 'Delete Group'}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {!deleteDialog.hasWarning ? (
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to delete the group <strong>"{deleteDialog.groupName}"</strong>?
            </Typography>
          ) : (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  This group has {deleteDialog.userCount} user(s) assigned
                </Typography>
                <Typography variant="body2">
                  These users will <strong>LOSE ACCESS</strong> to all pages granted by this group.
                </Typography>
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Affected Users:
              </Typography>
              <List dense sx={{
                bgcolor: 'background.default',
                borderRadius: 1,
                maxHeight: 200,
                overflow: 'auto'
              }}>
                {deleteDialog.userEmails.map((email, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={email}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontFamily: 'monospace' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 500 }}>
                Do you want to proceed with deletion?
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={deleting !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color={deleteDialog.hasWarning ? 'error' : 'primary'}
            disabled={deleting !== null}
            startIcon={deleting !== null ? <CircularProgress size={20} /> : null}
          >
            {deleting !== null ? 'Deleting...' : (deleteDialog.hasWarning ? 'Delete Anyway' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Users Dialog */}
      <Dialog
        open={manageUsersDialog.open}
        onClose={handleManageUsersClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PeopleIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Manage Users
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {manageUsersDialog.groupName}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {manageUsersDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Check users to add them to this group. Uncheck to remove them.
              </Typography>

              {/* Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or email..."
                value={manageUsersDialog.searchTerm}
                onChange={(e) => setManageUsersDialog(prev => ({ ...prev, searchTerm: e.target.value }))}
                sx={{ mb: 2 }}
              />

              <List dense sx={{
                bgcolor: 'background.default',
                borderRadius: 1,
                maxHeight: 400,
                overflow: 'auto'
              }}>
                {(() => {
                  // Filter users based on search term
                  const filteredUsers = manageUsersDialog.users.filter(user => {
                    if (!manageUsersDialog.searchTerm) return true;
                    const searchLower = manageUsersDialog.searchTerm.toLowerCase();
                    const fullName = `${user.user_first_name || ''} ${user.user_last_name || ''}`.trim().toLowerCase();
                    const email = (user.user_email || '').toLowerCase();
                    return fullName.includes(searchLower) || email.includes(searchLower);
                  });

                  if (filteredUsers.length === 0) {
                    return (
                      <ListItem>
                        <ListItemText
                          primary={manageUsersDialog.searchTerm ? "No users found" : "No users available"}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: 'text.secondary',
                            fontStyle: 'italic'
                          }}
                        />
                      </ListItem>
                    );
                  }

                  return filteredUsers.map((user) => (
                    <ListItem
                      key={user.user_id}
                      sx={{
                        py: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleUserToggle(user.user_id)}
                    >
                      <Checkbox
                        checked={user.in_group}
                        onChange={() => handleUserToggle(user.user_id)}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            {user.user_first_name || user.user_last_name
                              ? `${user.user_first_name || ''} ${user.user_last_name || ''}`.trim()
                              : 'No Name'}
                          </Typography>
                        }
                        secondary={user.user_email}
                        primaryTypographyProps={{
                          variant: 'body2'
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          sx: { fontFamily: 'monospace', fontSize: '0.7rem' }
                        }}
                      />
                    </ListItem>
                  ));
                })()}
              </List>

              {manageUsersDialog.users.some(u => u.was_in_group !== u.in_group) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {manageUsersDialog.users.filter(u => u.in_group && !u.was_in_group).length > 0 && (
                      <span>
                        <strong>{manageUsersDialog.users.filter(u => u.in_group && !u.was_in_group).length}</strong> user(s) will be added.
                      </span>
                    )}
                    {manageUsersDialog.users.filter(u => !u.in_group && u.was_in_group).length > 0 && (
                      <span>
                        {' '}
                        <strong>{manageUsersDialog.users.filter(u => !u.in_group && u.was_in_group).length}</strong> user(s) will be removed.
                      </span>
                    )}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleManageUsersClose}
            variant="outlined"
            disabled={manageUsersDialog.saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleManageUsersSave}
            variant="contained"
            color="primary"
            disabled={
              manageUsersDialog.saving ||
              manageUsersDialog.loading ||
              !manageUsersDialog.users.some(u => u.was_in_group !== u.in_group)
            }
            startIcon={manageUsersDialog.saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {manageUsersDialog.saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Groups;
